import { isObject } from 'lodash'

function __mergeDeep (target, source) {
  if (!isObject(target) || !isObject(source)) {
    return source
  }

  Object.keys(source).forEach(key => {
    const targetValue = target[key]
    const sourceValue = source[key]

    if (isObject(targetValue) && isObject(sourceValue)) {
      target[key] = __mergeDeep(Object.assign({}, targetValue), sourceValue)
    } else {
      target[key] = sourceValue
    }
  })

  return target
}

export const mergeUpdate = (source, update) => {
  return __mergeDeep(Object.assign({}, source), update)
}

/*
  Main Helper function for merging EVENT_ODDS_UPDATE topic payload with event

  Update with keys marketTypeId & outcomeIds (marketsOutcomesOddsUpdate)
  @example data {
    "2": {
      "33376863": {
        odds: 2.05
      },
      "33377038": {
        odds: 2.1
      },
      "33377140": {
        odds: 5.25
      }
    },
    "4": {
      "33374163": {
        odds: 1.18
      },
      "33374168": {
        odds: 3.8
      }
    }
  }
*/
export const mergeBatchOutcomesUpdateToEvent = (event, marketsOutcomesOddsUpdate) => {
  const eventMarkets = event.markets
  const updateIds = Object.keys(marketsOutcomesOddsUpdate)

  const updatedMarkets = Object.keys(eventMarkets)
    .reduce((acc, marketTypeKey) => {
      // If id exists in update object
      if (!updateIds.includes(marketTypeKey)) {
        // Returning unchanged marketType
        return {
          ...acc,
          [marketTypeKey]: eventMarkets[marketTypeKey]
        }
      }

      return {
        ...acc,
        [marketTypeKey]: mergeBatchOutcomesUpdateToMarket(
          eventMarkets[marketTypeKey], // Event.market {market}
          marketsOutcomesOddsUpdate[marketTypeKey] // Payload.outcome {update}
        )
      }
    }, {})

  return {
    ...event,
    markets: updatedMarkets
  }
}

/*
  Helper function for merging OutcomeOddsUpdate with Outcome
  Used in mergeBatchOutcomesUpdateToEvent
*/
export const mergeBatchOutcomesUpdateToMarket = (market, outcomeOddsUpdate) => {
  const marketOutcomes = market.outcomes
  const updateIds = Object.keys(outcomeOddsUpdate)

  const updatedOutcomes = Object.keys(marketOutcomes)
    .reduce((acc, outcomeId) => {
      const marketOutcome = marketOutcomes[outcomeId]
      // If id exists in update object
      if (!updateIds.includes(outcomeId)) {
        // Returing unchanged market outcome
        return {
          ...acc,
          [outcomeId]: marketOutcome
        }
      }

      return {
        ...acc,
        [outcomeId]: {
          ...marketOutcome, // Event.market.outcome {state}
          ...outcomeOddsUpdate[outcomeId] // Payload.odds {update}
        }
      }
    }, {})

  return {
    ...market,
    outcomes: updatedOutcomes
  }
}

/*
  Helper function for merging EVENT_MARKET_STATUS_UPDATES topic payload with event

  Topic payload example:
  {
    eventId: 448
    marketId: 51992
    marketStatus: "DEACTIVATED"
    marketTypeId: 85
    outcomeIds: [107509, 107510]
  }
*/
export const mergeMarketStatusUpdateToEvent = (event, marketStatusUpdate) => {
  const {
    marketTypeId,
    // eventId,
    marketStatus,
    outcomeIds
  } = marketStatusUpdate

  const eventMarketType = event.markets[marketTypeId]

  if (!eventMarketType) {
    return event
  }

  const eventMarketTypeOutcomes = outcomeIds.reduce(
    (outcomes, outcomeId) => {
      const outcome = eventMarketType.outcomes[outcomeId]
      if (!outcome) {
        return outcomes
      }

      return {
        ...outcomes,
        [outcomeId]: {
          ...outcome,
          marketStatus
        }
      }
    },
    eventMarketType.outcomes
  )

  return {
    ...event,
    markets: {
      ...event.markets,
      [marketTypeId]: {
        ...eventMarketType,
        outcomes: eventMarketTypeOutcomes
      }
    }
  }
}

/*
  Helper function for merging EVENT_OUTCOME_STATUS_UPDATES topic payload with event

  Topic payload example:
  {
    eventId: 406
    marketId: 52184
    outcomeId: 107914
    marketTypeId: 85
    outcomeStatus: "UNSPECIFIED"
  }
*/
export const mergeOutcomeStatusUpdateToEvent = (event, outcomeStatusUpdate) => {
  const {
    // marketTypeId,
    // eventId,
    // outcomeStatus,
    // outcomeId
    outcomeStatusesByMarketTypeId
  } = outcomeStatusUpdate

  return mergeBatchOutcomesUpdateToEvent(
    event,
    outcomeStatusesByMarketTypeId
  )
}

/*
  OutcomeOddsUpdate payload example {
    eventId: 103251,
    tournamentId: 2202,
    outcomeId: 25540885,
    marketTypeId: 1,
    odds: 1.55,
    isMain: true
  }
*/
export const mergeOutcomeOddsUpdateToEvent = (event, outcomeOddsUpdate) => {
  const {
    marketTypeId,
    outcomeId,
    odds,
    isMain
  } = outcomeOddsUpdate

  const eventMarketType = event.markets[marketTypeId]

  if (!eventMarketType || !eventMarketType.outcomes[outcomeId]) {
    return event
  }

  const eventMarketTypeOutcomes = {
    ...eventMarketType.outcomes,
    [outcomeId]: {
      ...eventMarketType.outcomes[outcomeId],
      odds,
      isMain
    }
  }

  return {
    ...event,
    markets: {
      ...event.markets,
      [marketTypeId]: {
        ...eventMarketType,
        outcomes: eventMarketTypeOutcomes
      }
    }
  }
}

export const mergeMarketSuspensionUpdateToEvent = (event, marketUpdate) => {
  const {
    marketTypeId,
    marketId,
    suspended
  } = marketUpdate

  const eventMarketType = event.markets[marketTypeId]

  if (!eventMarketType) {
    return event
  }

  const eventMarketTypeOutcomes = Object.keys(eventMarketType.outcomes)
    .reduce(
      (acc, outcomeId) => {
        const outcome = eventMarketType.outcomes[outcomeId]
        return {
          ...acc,
          [outcomeId]: (outcome.marketId !== marketId)
            ? outcome
            : { ...outcome, marketSuspended: suspended }
        }
      },
      {}
    )

  return {
    ...event,
    markets: {
      ...event.markets,
      [marketTypeId]: {
        ...eventMarketType,
        outcomes: eventMarketTypeOutcomes
      }
    }
  }
}

export const mergeMarketVisibilityUpdateToEvent = (event, marketUpdate) => {
  const {
    marketTypeId,
    marketId,
    visible
  } = marketUpdate

  const eventMarketType = event.markets[marketTypeId]

  if (!eventMarketType) {
    return event
  }

  const eventMarketTypeOutcomes = Object.keys(eventMarketType.outcomes)
    .reduce(
      (acc, outcomeId) => {
        const outcome = eventMarketType.outcomes[outcomeId]
        return {
          ...acc,
          [outcomeId]: (outcome.marketId !== marketId)
            ? outcome
            : { ...outcome, marketVisible: visible }
        }
      },
      {}
    )

  return {
    ...event,
    markets: {
      ...event.markets,
      [marketTypeId]: {
        ...eventMarketType,
        outcomes: eventMarketTypeOutcomes
      }
    }
  }
}
